import { Autocomplete, Avatar, Box, TextField, useTheme } from '@mui/material'
import { GridRenderEditCellParams } from '@mui/x-data-grid-premium'
import React, { useState } from 'react'
import { User } from 'types'

interface EditUserSelectCellProps {
  params: GridRenderEditCellParams
  users: User[]
}

const EditUserSelectCell: React.FC<EditUserSelectCellProps> = ({ params, users }) => {
  const theme = useTheme()
  const [open, setOpen] = useState(true)

  // Find current user from the user_uuid value
  const currentUser = users.find(u => u.user_uuid === params.value) || null

  const handleChange = (_event: React.SyntheticEvent, newValue: User | null) => {
    // Set the value to user_uuid (or null if cleared)
    params.api.setEditCellValue({
      id: params.id,
      field: params.field,
      value: newValue?.user_uuid ?? null,
    })
    // Stop edit mode
    params.api.stopCellEditMode({ id: params.id, field: params.field })
  }

  const handleClose = () => {
    setOpen(false)
    params.api.stopCellEditMode({ id: params.id, field: params.field })
  }

  return (
    <Autocomplete
      open={open}
      onOpen={() => setOpen(true)}
      onClose={handleClose}
      options={users}
      value={currentUser}
      onChange={handleChange}
      getOptionLabel={option => option?.name || ''}
      isOptionEqualToValue={(option, value) => option?.user_uuid === value?.user_uuid}
      autoFocus
      fullWidth
      size="small"
      renderOption={(props, option) => {
        const { key, ...rest } = props
        return (
          <Box component="li" key={key} {...rest} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar src={option?.image_512} alt={option?.name} sx={{ width: 24, height: 24 }} />
            <span>{option?.name}</span>
          </Box>
        )
      }}
      renderInput={inputParams => (
        <TextField
          {...inputParams}
          autoFocus
          variant="outlined"
          size="small"
          placeholder="Select user..."
          sx={{
            '& .MuiOutlinedInput-root': {
              padding: '2px 8px',
              bgcolor: theme.palette.background.paper,
            },
          }}
        />
      )}
      slotProps={{
        popper: {
          sx: {
            minWidth: 250,
          },
          placement: 'bottom-start',
          modifiers: [
            {
              name: 'flip',
              enabled: true,
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'viewport',
              },
            },
          ],
        },
        paper: {
          sx: {
            bgcolor: theme.palette.background.paper,
            minWidth: 250,
            '& .MuiAutocomplete-listbox': {
              minWidth: 250,
            },
          },
        },
      }}
      sx={{
        width: '100%',
        '& .MuiAutocomplete-inputRoot': {
          padding: '2px 8px',
          bgcolor: theme.palette.background.paper,
        },
      }}
    />
  )
}

export default EditUserSelectCell
